import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonChip,
  IonLabel,
  IonList,
  IonItem,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonAlert,
} from '@ionic/react';
import { addOutline, chatbubbleOutline, sparklesOutline, trashOutline } from 'ionicons/icons';
import { useAiConversations } from '../../application/use-cases/ai/useAiConversations';
import type { FieldAnalysisStatus } from '../../domain/Ai';
import './AiAssistant.css';

const STATUS_LABEL: Record<FieldAnalysisStatus, string> = {
  normal: 'Normal',
  attention: 'A vigilar',
  critical: 'Crítico',
  unknown: 'Sin datos suficientes',
};

const STATUS_COLOR: Record<FieldAnalysisStatus, string> = {
  normal: 'success',
  attention: 'warning',
  critical: 'danger',
  unknown: 'medium',
};

const AiConversationList: React.FC = () => {
  const history = useHistory();
  const {
    sites,
    selectedSiteId,
    setSelectedSiteId,
    loadingSites,
    conversations,
    loadingConversations,
    error,
    sitesError,
    retrySites,
    aiDisabled,
    quickLoading,
    quickReply,
    startConversation,
    deleteConversation,
    analyzeField,
    dailySummary,
  } = useAiConversations();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <IonIcon icon={sparklesOutline} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Asistente Domiot
          </IonTitle>
        </IonToolbar>
        {sites.length > 1 && (
          <IonToolbar>
            <IonSelect
              value={selectedSiteId}
              placeholder="Campo"
              onIonChange={(e) => setSelectedSiteId(e.detail.value)}
              className="ion-padding-horizontal"
            >
              {sites.map((s) => (
                <IonSelectOption key={s.id} value={s.id}>
                  {s.shared ? `${s.name} (de ${s.ownerName})` : s.name}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loadingSites && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loadingSites && sitesError && sites.length === 0 && (
          <IonCard>
            <IonCardContent>
              <IonText color="danger">
                <p style={{ marginTop: 0 }}>{sitesError}</p>
              </IonText>
              <IonButton expand="block" fill="outline" onClick={retrySites}>
                Reintentar
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}

        {!loadingSites && !sitesError && sites.length === 0 && (
          <IonCard>
            <IonCardContent>
              <p style={{ marginTop: 0 }}>
                El asistente responde sobre un campo y las estaciones que tiene asignadas. Creá uno y asignale tu
                estación para empezar.
              </p>
              <IonButton expand="block" fill="outline" routerLink="/app/sites">
                Crear un campo
              </IonButton>
            </IonCardContent>
          </IonCard>
        )}

        {!loadingSites && sites.length > 0 && (
          <>
            {aiDisabled && (
              <IonCard>
                <IonCardContent>
                  <p>El asistente de IA todavía no está activado para tu cuenta.</p>
                </IonCardContent>
              </IonCard>
            )}

            <div className="ai-quick-actions">
              <IonButton fill="outline" size="small" disabled={quickLoading} onClick={() => startConversation('¿Cómo está mi campo?')}>
                ¿Cómo está mi campo?
              </IonButton>
              <IonButton fill="outline" size="small" disabled={quickLoading} onClick={analyzeField}>
                Ver anomalías
              </IonButton>
              <IonButton fill="outline" size="small" disabled={quickLoading} onClick={dailySummary}>
                Resumen de hoy
              </IonButton>
            </div>

            {quickLoading && (
              <div className="ion-text-center ion-padding">
                <IonSpinner color="primary" />
              </div>
            )}

            {error && (
              <IonText color="danger">
                <p>{error}</p>
              </IonText>
            )}

            {quickReply && !quickLoading && (
              <IonCard>
                <IonCardContent>
                  {quickReply.kind === 'text' ? (
                    <p className="ai-reply-text">{quickReply.text}</p>
                  ) : (
                    <>
                      <IonChip color={STATUS_COLOR[quickReply.analysis.status]}>
                        <IonLabel>{STATUS_LABEL[quickReply.analysis.status]}</IonLabel>
                      </IonChip>
                      <p className="ai-reply-text">{quickReply.analysis.summary}</p>
                      {quickReply.analysis.anomalies.length > 0 && (
                        <>
                          <h3>Anomalías</h3>
                          <ul>
                            {quickReply.analysis.anomalies.map((a, i) => (
                              <li key={i}>{a}</li>
                            ))}
                          </ul>
                        </>
                      )}
                      {quickReply.analysis.recommendations.length > 0 && (
                        <>
                          <h3>Recomendaciones</h3>
                          <ul>
                            {quickReply.analysis.recommendations.map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                          </ul>
                        </>
                      )}
                      {quickReply.analysis.dataLimitations.length > 0 && (
                        <p className="ai-limitations">{quickReply.analysis.dataLimitations.join(' ')}</p>
                      )}
                    </>
                  )}
                </IonCardContent>
              </IonCard>
            )}

            <IonButton expand="block" className="ai-new-conversation" onClick={() => startConversation()}>
              <IonIcon icon={addOutline} slot="start" />
              Nueva conversación
            </IonButton>

            {loadingConversations && (
              <div className="ion-text-center ion-padding">
                <IonSpinner color="primary" />
              </div>
            )}

            {!loadingConversations && conversations.length > 0 && (
              <IonList className="ai-conversation-list">
                {conversations.map((c) => (
                  <IonItemSliding key={c.id}>
                    <IonItem lines="full">
                      <IonIcon icon={chatbubbleOutline} slot="start" color="medium" />
                      <IonLabel className="ai-conversation-label" onClick={() => history.push(`/app/ai/${c.id}`)}>
                        <h2>{c.title}</h2>
                        <p>{new Date(c.updatedAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                      </IonLabel>
                      <IonButton fill="clear" color="medium" slot="end" onClick={() => setPendingDeleteId(c.id)} aria-label="Borrar conversación">
                        <IonIcon icon={trashOutline} slot="icon-only" />
                      </IonButton>
                    </IonItem>
                    <IonItemOptions side="end">
                      <IonItemOption color="danger" onClick={() => setPendingDeleteId(c.id)}>
                        <IonIcon icon={trashOutline} slot="icon-only" />
                      </IonItemOption>
                    </IonItemOptions>
                  </IonItemSliding>
                ))}
              </IonList>
            )}
          </>
        )}

        <IonAlert
          isOpen={pendingDeleteId !== null}
          onDidDismiss={() => setPendingDeleteId(null)}
          header="¿Borrar conversación?"
          message="Esta acción no se puede deshacer."
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            {
              text: 'Borrar',
              role: 'destructive',
              handler: () => {
                if (pendingDeleteId) deleteConversation(pendingDeleteId);
              },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default AiConversationList;
